// Lightweight language context — EN/ES toggle.
// Components read t(key) to get translated strings; default is EN.
import { createContext, useContext, useState, useEffect } from "react";

const STRINGS = {
  en: {
    "nav.story": "Story",
    "nav.menu": "Menu",
    "nav.brunch": "Brunch",
    "nav.signature": "The $29",
    "nav.order": "Order",
    "nav.gallery": "Gallery",
    "nav.visit": "Visit",
    "nav.reserve": "Reserve",

    "hero.kicker": "— A Homestyle Kitchen —",
    "hero.location": "Houston, Texas · Est. 2020",
    "hero.tagline":
      "Soul, Spanish & Mediterranean cooking — served with brasserie posture and Texas hospitality. Brunch under the chandeliers, dinner by candlelight.",
    "hero.now_serving": "Now Serving",
    "hero.quote": "\"Brunch, devotion & a little champagne.\"",
    "hero.cta_reserve": "Reserve a Table",
    "hero.cta_menu": "Browse the Menu",
    "hero.scroll": "Scroll",

    "story.kicker": "— Chapter One",
    "story.heading_1": "A kitchen",
    "story.heading_2": "with a memory.",
    "story.stat_cuisines": "Cuisines",
    "story.stat_dishes": "House Dishes",
    "story.stat_table": "Long Table",

    "menu.kicker": "— The Carte",
    "menu.heading": "The Menu.",
    "menu.subtitle":
      "Twelve sections, more than one hundred and sixty dishes. We change quietly with the season — ask your server about today's hand-written additions.",
    "menu.mp_note": "mp · market price, hand-written daily.",
    "menu.dishes": "dishes",

    "signature.kicker": "— From the bar",
    "signature.cta_primary": "Reserve for the $29",
    "signature.cta_secondary": "Full Menu",
    "signature.badge": "House Signature",

    "brunch.kicker": "— Sundays & Saturdays · 10 → 14",
    "brunch.heading_1": "Brunch at",
    "brunch.heading_2": "Ladiosa's.",
    "brunch.cta": "Book the Brunch Table",

    "order.kicker": "— Takeout & Pickup",
    "order.heading": "Order to go.",
    "order.subtitle":
      "Build a takeout order and we'll have it ready when you arrive. Pickup runs from 11:30 to 21:30 daily.",
    "order.add": "Add",
    "order.remove": "Remove",
    "order.cart_title": "Your Order",
    "order.cart_empty": "Your basket is empty. Add a few dishes to get started.",
    "order.submit": "Place Pickup Order",
    "order.success_title": "Order received.",

    "events.kicker": "— Private Hire & Buyouts",
    "events.heading": "Private events.",
    "events.subtitle":
      "Long tables for engagements, anniversaries and full restaurant buyouts. Our private dining manager replies within one business day.",
    "events.submit": "Send Inquiry",
    "events.success": "We'll be in touch within one business day.",

    "press.kicker": "— Pinned to the Pass",
    "press.heading": "What they're saying.",

    "gallery.kicker": "— The House Archive",
    "gallery.heading": "Around the room.",
    "gallery.subtitle":
      "A scrapbook from the kitchen, the bar, and the long marble pass — updated weekly.",

    "reserve.kicker": "— A Table for You",
    "reserve.heading": "Reserve.",
    "reserve.submit": "Request a Table",
    "reserve.sending": "Sending…",
    "reserve.success_title": "Merci",
    "reserve.new": "Make Another Reservation",

    "visit.kicker": "— Find Us",
    "visit.heading_1": "Visit the",
    "visit.heading_2": "brasserie.",

    "footer.bulletin_title": "The House Bulletin",
    "footer.bulletin_copy":
      "New dishes, brunch dates, and the occasional secret menu — monthly, never spammy.",
    "footer.join": "Join",
  },
  es: {
    "nav.story": "Historia",
    "nav.menu": "Carta",
    "nav.brunch": "Brunch",
    "nav.signature": "El de $29",
    "nav.order": "Pedir",
    "nav.gallery": "Galería",
    "nav.visit": "Visita",
    "nav.reserve": "Reservar",

    "hero.kicker": "— Una Cocina de Casa —",
    "hero.location": "Houston, Texas · Est. 2020",
    "hero.tagline":
      "Cocina Soul, Española y Mediterránea — servida con elegancia de brasserie y hospitalidad tejana. Brunch bajo las arañas, cena a la luz de las velas.",
    "hero.now_serving": "Sirviendo Ahora",
    "hero.quote": "\"Brunch, devoción y un poco de champán.\"",
    "hero.cta_reserve": "Reservar Mesa",
    "hero.cta_menu": "Ver la Carta",
    "hero.scroll": "Desplaza",

    "story.kicker": "— Capítulo Uno",
    "story.heading_1": "Una cocina",
    "story.heading_2": "con memoria.",
    "story.stat_cuisines": "Cocinas",
    "story.stat_dishes": "Platos de Casa",
    "story.stat_table": "Mesa Larga",

    "menu.kicker": "— La Carta",
    "menu.heading": "La Carta.",
    "menu.subtitle":
      "Doce secciones, más de ciento sesenta platos. Cambiamos en silencio con la temporada — pregunta por las novedades del día.",
    "menu.mp_note": "mp · precio del día, escrito a mano.",
    "menu.dishes": "platos",

    "signature.kicker": "— Desde la barra",
    "signature.cta_primary": "Reservar el de $29",
    "signature.cta_secondary": "Carta Completa",
    "signature.badge": "Especialidad de Casa",

    "brunch.kicker": "— Sábados y Domingos · 10 → 14",
    "brunch.heading_1": "Brunch en",
    "brunch.heading_2": "Ladiosa's.",
    "brunch.cta": "Reservar Brunch",

    "order.kicker": "— Para Llevar",
    "order.heading": "Pedir para llevar.",
    "order.subtitle":
      "Arma un pedido para llevar y lo tendremos listo a tu llegada. Recogida de 11:30 a 21:30 todos los días.",
    "order.add": "Añadir",
    "order.remove": "Quitar",
    "order.cart_title": "Tu Pedido",
    "order.cart_empty": "Tu cesta está vacía. Añade algunos platos para empezar.",
    "order.submit": "Hacer Pedido",
    "order.success_title": "Pedido recibido.",

    "events.kicker": "— Eventos Privados",
    "events.heading": "Eventos privados.",
    "events.subtitle":
      "Mesas largas para compromisos, aniversarios y reservaciones completas. Nuestro gerente responde en un día hábil.",
    "events.submit": "Enviar Consulta",
    "events.success": "Te contactaremos en un día hábil.",

    "press.kicker": "— Anotado en el Pase",
    "press.heading": "Lo que dicen.",

    "gallery.kicker": "— Archivo de la Casa",
    "gallery.heading": "Por la sala.",
    "gallery.subtitle":
      "Un cuaderno de la cocina, la barra y el largo mostrador de mármol — actualizado cada semana.",

    "reserve.kicker": "— Una Mesa Para Ti",
    "reserve.heading": "Reservar.",
    "reserve.submit": "Solicitar Mesa",
    "reserve.sending": "Enviando…",
    "reserve.success_title": "Gracias",
    "reserve.new": "Hacer Otra Reservación",

    "visit.kicker": "— Encuéntranos",
    "visit.heading_1": "Visita la",
    "visit.heading_2": "brasserie.",

    "footer.bulletin_title": "Boletín de la Casa",
    "footer.bulletin_copy":
      "Nuevos platos, fechas de brunch y a veces un menú secreto — mensual, nunca invasivo.",
    "footer.join": "Suscribir",
  },
};

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
});

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return window.localStorage.getItem("ldo_lang") || "en";
  });

  useEffect(() => {
    window.localStorage.setItem("ldo_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key) => STRINGS[lang]?.[key] ?? STRINGS.en[key] ?? key;
  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
